// MUI-free copy of Ui-components/card-technology.
import React, {memo} from 'react';
import type {CSSProperties} from 'react';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import Eyebrow from '@/components/atoms/Eyebrow';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import {spacing} from '@/styles/tokens';
import styles from './CardTechnology.module.scss';

export interface CardTechnologyItem {
    icon: string | StaticImageData;
    label: string;
}

export interface CardTechnologyCard {
    heading: string;
    description?: string;
    values: CardTechnologyItem[];
}

interface CardTechnologyProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    data: CardTechnologyCard[];
    /**
     * Fixes the icons to this many per row on desktop instead of letting them
     * wrap freely. Mobile always falls back to a narrower grid.
     */
    iconsPerRow?: number;
    /** Icon box size, as a `spacing` token key. Defaults to 56. */
    iconSize?: keyof typeof spacing;
    /** Card side padding, as a `spacing` token key. Defaults to 24 (same as the vertical padding). */
    cardPaddingX?: keyof typeof spacing;
}

// Same as the original's `style` on the fill image.
const ICON_IMAGE_STYLE: CSSProperties = {objectFit: 'contain', padding: spacing[4]};

const CardTechnology = ({eyebrow, heading, subheading, data, iconsPerRow, iconSize = 56, cardPaddingX = 24}: CardTechnologyProps) => {
    // Per-instance sizes go in through CSS variables; the module supplies the defaults (56 / 24).
    const cardStyle = {
        ...(cardPaddingX !== 24 && {'--card-px': spacing[cardPaddingX]}),
        ...(iconSize !== 56 && {'--icon-size': spacing[iconSize]})
    } as CSSProperties;
    const iconsStyle = iconsPerRow ? ({'--icons-per-row': iconsPerRow} as CSSProperties) : undefined;

    return (
        <div>
            <Container>
                <div className={styles.header}>
                    {eyebrow && <Eyebrow text={eyebrow} />}
                    <h2 className={`font-headers ${styles.heading}`}>{parse(heading)}</h2>
                    {subheading && <p className={`font-regular-text ${styles.subheading}`}>{parse(subheading)}</p>}
                </div>

                <div className={styles.grid}>
                    {data?.map((card, index) => (
                        <div className={styles.item} key={index}>
                            <div className={styles.card} style={cardStyle}>
                                <div className={styles.cardHead}>
                                    <h3 className={`font-headers ${styles.cardHeading}`}>{parse(card.heading)}</h3>
                                    {card.description && <p className={`font-regular-text ${styles.cardDesc}`}>{card.description}</p>}
                                </div>

                                <div className={iconsPerRow ? styles.iconsGrid : styles.iconsWrap} style={iconsStyle}>
                                    {card.values?.map((item, i) => (
                                        <div key={i} className={iconsPerRow ? styles.iconGridItem : styles.iconItem}>
                                            <div className={styles.iconBox}>
                                                <OptimizedImage
                                                    src={item.icon}
                                                    alt={item.label}
                                                    fill
                                                    style={ICON_IMAGE_STYLE}
                                                    priority={false}
                                                />
                                            </div>
                                            <p className={styles.iconLabel}>{item.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Container>
        </div>
    );
};

export default memo(CardTechnology);
